import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import * as fromStore from '../../../store';
import { CompanyInfo } from '../../../store/actions/company-info.action';

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalytics4Service {

  private _isInitialized = false;
  private _currentClientId: string | null = null;
  private _pendingClientId: string | null = null;
  private gaId: string | null = null;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _router: Router,
    private _store: Store<fromStore.DbStoreState>
  ) {
    this._initializeFromCompanyInfo();
    this._setupAutoPageViews();
  }

  private _setupAutoPageViews(): void {
    // Listen to Angular route changes and trigger GA4 page_view events
    this._router.events.pipe(
        filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (this._isInitialized) {
        const gtag = window['gtag'];
        if (typeof gtag === 'function') {
          const eventParams: any = {
            page_path: event.urlAfterRedirects,
            page_location: window.location.href,
            page_title: this._document.title
          };

          // Include twdb_user_id if available
          if (this._currentClientId) {
            eventParams.twdb_user_id = this._currentClientId;
          }

          gtag('event', 'page_view', eventParams);
        }
      }
    });
  }

  private _initializeFromCompanyInfo(): void {
    this._store.select(fromStore.getCompanyInfo)
      .pipe(
        filter((companyInfo: CompanyInfo) => !!companyInfo),
        take(1)
      )
      .subscribe((companyInfo: CompanyInfo) => {
        this.gaId = companyInfo.branding?.google_analytics_id;

        if (this.gaId && !this._isInitialized) {
          this._injectGA4Script(this.gaId);
        }

        if (this._isInitialized && this._pendingClientId) {
          const pending = this._pendingClientId;
          this._pendingClientId = null;
          this.setClientId(pending);
        }
      });
  }

  private _injectGA4Script(measurementId: string): void {
    const ga4Pattern = /^G-[A-Z0-9]{10}$/;
    if (!ga4Pattern.test(measurementId)) {
      console.warn(`[GA4] Invalid measurement ID format: ${measurementId}`);
      return;
    }

    if (this._document.getElementById('ga-script')) {
      return;
    }

    const gtagScript = this._document.createElement('script');
    gtagScript.id = 'ga-script';
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    this._document.head.appendChild(gtagScript);

    const configScript = this._document.createElement('script');
    configScript.id = 'ga-config';
    configScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}', {
        send_page_view: false
      });
    `;
    this._document.head.appendChild(configScript);

    this._isInitialized = true;
  }

  public clearClientId(): void {
    this._currentClientId = null;
    this._pendingClientId = null;

    const gtag = window['gtag'];
    if (typeof gtag === 'function') {
      gtag('set', 'user_id', undefined);
    }
  }

  public setClientId(clientId: string | number): void {
    if (!clientId) {
      return;
    }

    const clientIdString = clientId.toString();

    if (!this._isInitialized) {
      this._pendingClientId = clientIdString;
      return;
    }

    // Only update if it's different from the current one
    if (this._currentClientId === clientIdString) {
      return;
    }

    this._currentClientId = clientIdString;

    const gtag = window['gtag'];
    if (typeof gtag === 'function') {
      // Set user_id for GA4 user identification
      gtag('set', 'user_id', clientIdString);

      // Send a page_view event with twdb_user_id
      gtag('event', 'page_view', {
        twdb_user_id: clientIdString,
        page_location: window.location.href,
        page_title: this._document.title
      });
    }
  }

}
